import {
  GameShootItAdvancedData,
  GameShootItData,
  GameShootItGeneralData,
  GameShootItImagesSymbolData
} from '@/components/games/shootit/Data';
import useDevice from '@/hooks/useDevice';
import { GameIndicatorIcon, GameIndicatorSettingsModel } from '@/components/indicators/Model';
import { GameIndicator, GameModel, GameTimeChallenge, HasGameTimeChallenge } from '@/models/GameModel';
import { transformMeasurementToNumber } from '@/utilities/Utilities';
import { TransitionTypes } from '@/typings/types/types';

interface GameShootItDimensionsState {
  canvasHeight: number;
  canvasHeightIsViewPort: boolean;
  shooterOffsetY: number;
  shootSpeed: number;
  spawnSpeed: number;
  shooterWidth: number;
  dropWidth: number;
  overwriteMobile?: boolean;
}

interface GameShootItGeneralState {
  scoreToWin?: number;
  colorPoint?: string;
  colorShot?: string;
  dimensions: GameShootItDimensionsState;
  enableBestTime?: boolean;
  enableTimeRanking?: boolean;
}

export interface GameShootItImageSymbolState {
  image: string;
  points: number;
  health: number;
  speed: number;
  rotation: number;
  label: string;
  id: number;
  height?: number;
  width?: number;
}

export interface GameShootItAdvancedSoundState {
  enabled: boolean;
  plusSound?: string;
  minusSound?: string;
}

export interface GameShootItAdvancedAnimationState {
  enabled: boolean;
  type?: TransitionTypes;
}

export interface GameShootItAdvancedState {
  sound: GameShootItAdvancedSoundState;
  animation: GameShootItAdvancedAnimationState;
}

export interface GameShootItState {
  general: GameShootItGeneralState;
  elementStyling: {
    animationClass?: string;
    canvasStyle?: string;
  };
  images: {
    shooterImage: string;
    symbol: GameShootItImageSymbolState[];
  };
  advanced: GameShootItAdvancedState;
  indicators: GameIndicatorSettingsModel;
}

export class GameShootItModel extends GameModel<GameShootItData, GameShootItState> implements HasGameTimeChallenge {
  parseGame(data: GameShootItData) {
    const state = this.state;
    state.general = GameShootItModel.constructGeneralState(data.general);

    state.images = state.images || {};
    if (data.images.shooter_image) {
      state.images.shooterImage = data.images.shooter_image;
    }
    state.elementStyling = state.elementStyling ?? {};

    state.images.symbol = [];

    data.images.symbol.reverse().forEach((symbol) => {
      state.images.symbol.push(GameShootItModel.constructImagesState(symbol));
    });

    state.advanced = state.advanced ?? {};
    state.advanced.sound = GameShootItModel.constructAdvancedSoundState(data?.advanced);
    state.advanced.animation = GameShootItModel.constructAdvancedAnimationState(data?.advanced);

    if (data.indicators) {
      if (state.indicators) {
        state.indicators.setData(data.indicators);
      } else {
        state.indicators = new GameIndicatorSettingsModel(data.indicators);
      }
    }

    if (state.advanced.animation.enabled && state.advanced.animation.type) {
      state.elementStyling.animationClass = `animated ${state.advanced.animation.type}`;
    }

    let sizeMeasurement = 'px';
    if (state.general.dimensions.canvasHeightIsViewPort) {
      sizeMeasurement = 'vh';
    }

    state.elementStyling.canvasStyle = `height:${state.general.dimensions.canvasHeight}${sizeMeasurement}`;
  }

  private static constructAdvancedAnimationState(
    data: GameShootItAdvancedData | undefined
  ): GameShootItAdvancedAnimationState {
    return {
      enabled: !!data?.animation,
      ...(data?.animation && { type: data.animation })
    };
  }

  private static constructAdvancedSoundState(data: GameShootItAdvancedData | undefined): GameShootItAdvancedSoundState {
    return {
      enabled: !!data?.plus_sound || !!data?.minus_sound,
      ...(data?.minus_sound && { minusSound: data.minus_sound }),
      ...(data?.plus_sound && { plusSound: data.plus_sound })
    };
  }

  private static constructImagesState(data: GameShootItImagesSymbolData): GameShootItImageSymbolState {
    return {
      id: data.id,
      health: data.health,
      image: data.image,
      label: data.label,
      points: data.points,
      rotation: data.rotation,
      speed: data.speed
    };
  }

  private static parseDimensionsDeviceData(data: GameShootItGeneralData): GameShootItDimensionsState {
    return this.getMobileDeviceData(data);
  }

  public static getMobileDeviceData(data: GameShootItGeneralData): GameShootItDimensionsState {
    const { isMobile } = useDevice();

    let canvasHeight = transformMeasurementToNumber(data.dimensions.desktop.canvas_height);

    if (isMobile && data.dimensions.mobile) {
      canvasHeight = transformMeasurementToNumber(data.dimensions.mobile.canvas_height);

      return {
        canvasHeight: canvasHeight,
        canvasHeightIsViewPort: !!data.dimensions.mobile.canvas_height?.includes('vh'),
        shooterOffsetY: data.dimensions.mobile?.shooter_offset_y ?? 0,
        shootSpeed: data.dimensions.mobile.shoot_speed,
        spawnSpeed: data.dimensions.mobile.spawn_speed,
        shooterWidth: data.dimensions.mobile.shooter_width,
        dropWidth: data.dimensions.mobile.drop_width
      };
    }

    return {
      canvasHeight: canvasHeight,
      canvasHeightIsViewPort: !!data.dimensions.desktop.canvas_height?.includes('vh'),
      shooterOffsetY: data.dimensions.desktop?.shooter_offset_y ?? 0,
      shootSpeed: data.dimensions.desktop.shoot_speed,
      spawnSpeed: data.dimensions.desktop.spawn_speed,
      shooterWidth: data.dimensions.desktop.shooter_width,
      dropWidth: data.dimensions.desktop.drop_width
    };
  }

  private static constructGeneralState(data: GameShootItGeneralData): GameShootItGeneralState {
    return {
      ...(data?.score_towin && { scoreToWin: data.score_towin }),
      ...(data?.color_point && { colorPoint: data.color_point }),
      ...(data?.color_shot && { colorShot: data.color_shot }),
      ...(data?.enable_best_time && {
        enableBestTime: data.enable_best_time === '1'
      }),
      ...(data?.enable_time_ranking && {
        enableTimeRanking: data.enable_time_ranking === '1'
      }),

      dimensions: GameShootItModel.parseDimensionsDeviceData(data)
    };
  }

  public getIndicators(): GameIndicator[] {
    return [
      {
        indicatorKey: 'time',
        metricKey: {
          time_left: 'timeleft',
          time_used: 'timeused'
        },
        icon: GameIndicatorIcon.TIME,
        value: {
          time_left: this.state.timeChallenge?.limit ?? 0,
          time_used: 1
        }
      },
      {
        indicatorKey: 'score',
        metricKey: {
          score_used: 'score'
        },
        icon: GameIndicatorIcon.SCORE,
        value: {
          score_used: 0
        }
      }
    ];
  }

  parseTimeChallenge(): GameTimeChallenge | undefined {
    const data = this.getData();
    return {
      enabled: data.general.time === 1,
      ...(data.general.time_limit && { limit: Number(data.general.time_limit) })
    };
  }

  public isGameValid(): boolean {
    // is valid logic here
    // const data = this.getData();
    return true;
  }

  authorSignature(): string {
    return 'Sebastian Jakobsen';
  }
}
